// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Grid, {GridItem} from 'sail/Grid';
import {Text} from 'sail/Text';
import {usePrefs, useVideos, usePersistentState} from 'docs/data';
import type {VideoModel, VideoPlaylistModel} from 'docs/data';
import {useVideoPlayer} from 'docs/hooks';
import {PlaylistHeader} from './PlaylistHeader';
import {PlaylistTabs} from './PlaylistTabs';
import {PlaylistVideoSelector} from './PlaylistVideoSelector';

// Helper for extracting the videos from a VideoPlaylistModel based on
// the current user's preferences. It accepts the playlist and the getPref
// method from the usePrefs hook so that it can filter all videos in a chapter
// to only the list of videos where either the prefs match or the default
// video for the chapter is used.
const prefVideosForPlaylist = (
  playlist: VideoPlaylistModel,
  prefs: {lang: string},
): Array<VideoModel> => {
  // TODO: Add support for client, platform, etc. the other prefs
  return playlist.chapters.map((chapter) => {
    const prefMatchVideo = chapter.videos.find((video) => {
      return video.prefs.lang === prefs.lang;
    });

    if (prefMatchVideo) {
      return prefMatchVideo;
    }

    return chapter.defaultVideo;
  });
};

type PlaylistPlayerInnerProps = {|
  playlist: VideoPlaylistModel,
  currentVideo: VideoModel,
  onChangeVideo: (video: VideoModel) => any,
|};
const PlaylistPlayerInner = ({
  playlist,
  currentVideo,
  onChangeVideo,
}: PlaylistPlayerInnerProps) => {
  const {getPref} = usePrefs();
  const prefs = {
    lang: getPref('lang') || 'ruby',
  };
  const prefVideos = prefVideosForPlaylist(playlist, prefs);

  const {
    frame,
    isPlaying,
    playVideo,
    pauseVideo,
    getCurrentTime,
    currentTime,
    loadNewVideo,
  } = useVideoPlayer({video: currentVideo});

  const onPlayClick = () => {
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  };

  const changeVideo = (video: VideoModel) => {
    onChangeVideo(video);
    loadNewVideo(video.youtubeId);
    pauseVideo();
  };

  React.useEffect(() => {
    if (isPlaying) {
      // This polls the youtube API to get the current time, which updates
      // the current time state. This is threaded to the Video Transcript
      const interval = setInterval(() => {
        getCurrentTime();
      }, 200);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isPlaying, getCurrentTime]);

  return (
    <>
      <Text lineHeight={64} size={28} weight="bold">
        {currentVideo.title}
      </Text>

      <PlaylistHeader
        playlist={playlist}
        onChangeVideo={changeVideo}
        currentVideo={currentVideo}
      />

      <Box id={playlist.id} padding={{top: 32, bottom: 12}}>
        <Grid gap={32}>
          <GridItem columnSpan={8}>
            <Box style={{width: 720, height: 405}} onClick={onPlayClick}>
              {frame}
            </Box>
          </GridItem>
          <GridItem column={9} columnSpan={4}>
            <PlaylistVideoSelector
              prefVideos={prefVideos}
              onChangeVideo={changeVideo}
              currentVideo={currentVideo}
            />
          </GridItem>
        </Grid>
        <PlaylistTabs currentTime={currentTime} video={currentVideo} />
      </Box>
    </>
  );
};

type PlaylistPlayerProps = {|
  playlist: string,
  width?: number,
  height?: number,
  controls: boolean,
  id?: string,
  className?: string,
  children?: React.Node,
  title?: string,
|};
export const PlaylistPlayer = ({
  playlist,
  width = 720,
  height = 405,
  controls,
  id,
  className = '',
}: PlaylistPlayerProps) => {
  const {getPref} = usePrefs();

  // Fetch the playlist from the playlist data with the string ID given from markdoc.
  // Playlists are defined in: docs/videos/playlists.yaml
  const videos = useVideos({prefetchAllVideos: true});
  const fetchedPlaylist = videos.getPlaylist(playlist);

  // Load the current video's ID from persistent storage.
  const [currentVideoId, setCurrentVideoId, isLoading] = usePersistentState<
    string | null,
  >(`playlist-progress-${playlist}`, null);

  // Ensure the playlist is fetched and the current video isn't being read (async)
  // from persistent storage (IndexedDB)
  if (!fetchedPlaylist || isLoading) {
    return null;
  }

  // Get the relevant videos given this user's preferences.
  const prefs = {
    lang: getPref('lang') || 'ruby',
  };
  const prefVideos = prefVideosForPlaylist(fetchedPlaylist, prefs);

  // Lookup the current video based on the current video ID in persistent storage.
  const currentVideo =
    prefVideos.find((v) =>
      v.id.includes(currentVideoId || 'noCurrentVideoId'),
    ) || prefVideos[0];

  if (!currentVideo) {
    return null;
  }

  return (
    <>
      <PlaylistPlayerInner
        playlist={fetchedPlaylist}
        currentVideo={currentVideo}
        onChangeVideo={(newVideo) => {
          const topicId = newVideo.id.substring(
            0,
            newVideo.id.lastIndexOf('-'),
          );
          setCurrentVideoId(topicId);
        }}
      />
    </>
  );
};
